import type { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { languageGuard } from './core/guards/language.guard';
import { projectDetailResolver } from './features/projects/project-detail/project-detail.resolver';
import { blogPostResolver } from './features/blog/blog-post/blog-post.resolver';
import { landingResolver } from './features/landing/landing.resolver';

/**
 * All public routes live under `/:lang/...` so every page has a language prefix
 * in its URL (/es/projects, /en/blog, etc.).
 *
 * Legacy routes without the prefix redirect to `/es/...` for backward compatibility.
 * Auth routes (login, admin) stay at the root level — no language prefix needed.
 */

const publicChildren: Routes = [
  {
    path: '',
    resolve: { landing: landingResolver },
    loadComponent: () =>
      import('./features/landing/landing.component').then((m) => m.LandingComponent),
  },
  {
    path: 'projects',
    loadComponent: () =>
      import('./features/projects/project-list/project-list.component').then(
        (m) => m.ProjectListComponent,
      ),
  },
  {
    path: 'projects/:slug',
    resolve: { project: projectDetailResolver },
    loadComponent: () =>
      import('./features/projects/project-detail/project-detail.component').then(
        (m) => m.ProjectDetailComponent,
      ),
  },
  {
    path: 'cv',
    loadComponent: () =>
      import('./features/cv-viewer/cv-viewer.component').then((m) => m.CvViewerComponent),
  },
  {
    path: 'uses',
    loadComponent: () => import('./features/uses/uses.component').then((m) => m.UsesComponent),
  },
  {
    path: 'stack',
    loadComponent: () => import('./features/stack/stack.component').then((m) => m.StackComponent),
  },
  {
    path: 'now',
    loadComponent: () => import('./features/now/now.component').then((m) => m.NowComponent),
  },
  {
    path: 'lab',
    loadComponent: () => import('./features/lab/lab.component').then((m) => m.LabComponent),
  },
  {
    path: 'guestbook',
    loadComponent: () =>
      import('./features/guestbook/guestbook.component').then((m) => m.GuestbookComponent),
  },
  {
    path: 'battle',
    loadComponent: () =>
      import('./features/battle/battle.component').then((m) => m.BattleComponent),
  },
  {
    path: 'services',
    loadComponent: () =>
      import('./features/services/services.component').then((m) => m.ServicesComponent),
  },
  {
    path: 'contact',
    loadComponent: () =>
      import('./features/contact-page/contact-page.component').then((m) => m.ContactPageComponent),
  },
  {
    path: 'blog',
    loadComponent: () =>
      import('./features/blog/blog-list/blog-list.component').then((m) => m.BlogListComponent),
  },
  {
    path: 'blog/:slug',
    resolve: { post: blogPostResolver },
    loadComponent: () =>
      import('./features/blog/blog-post/blog-post.component').then((m) => m.BlogPostComponent),
  },
  {
    path: 'certifications/:id',
    loadComponent: () =>
      import('./features/certifications/certification-detail/certification-detail.component').then(
        (m) => m.CertificationDetailComponent,
      ),
  },
  {
    path: 'privacy',
    loadComponent: () =>
      import('./features/privacy/privacy.component').then((m) => m.PrivacyComponent),
  },
  {
    path: 'terminal',
    loadComponent: () =>
      import('./features/terminal/terminal.component').then((m) => m.TerminalComponent),
  },
  {
    path: 'showcase',
    loadComponent: () =>
      import('./features/showcase/showcase.component').then((m) => m.ShowcaseComponent),
  },
];

export const routes: Routes = [
  // Bare root -> default language
  { path: '', redirectTo: '/es', pathMatch: 'full' },

  // Auth routes — no language prefix
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'admin',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/admin/admin-layout/admin-layout.component').then(
        (m) => m.AdminLayoutComponent,
      ),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/admin/dashboard/admin-dashboard.component').then(
            (m) => m.AdminDashboardComponent,
          ),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/admin/profile-editor/profile-editor.component').then(
            (m) => m.ProfileEditorComponent,
          ),
      },
      {
        path: 'experiences',
        loadComponent: () =>
          import('./features/admin/experiences-editor/experiences-editor.component').then(
            (m) => m.ExperiencesEditorComponent,
          ),
      },
      {
        path: 'projects',
        loadComponent: () =>
          import('./features/admin/projects-editor/projects-editor.component').then(
            (m) => m.ProjectsEditorComponent,
          ),
      },
      {
        path: 'technologies',
        loadComponent: () =>
          import('./features/admin/technologies-editor/technologies-editor.component').then(
            (m) => m.TechnologiesEditorComponent,
          ),
      },
      {
        path: 'education',
        loadComponent: () =>
          import('./features/admin/education-editor/education-editor.component').then(
            (m) => m.EducationEditorComponent,
          ),
      },
      {
        path: 'certifications',
        loadComponent: () =>
          import('./features/admin/certifications-editor/certifications-editor.component').then(
            (m) => m.CertificationsEditorComponent,
          ),
      },
      {
        path: 'github',
        loadComponent: () =>
          import('./features/admin/github-manager/github-manager.component').then(
            (m) => m.GithubManagerComponent,
          ),
      },
      {
        path: 'social',
        loadComponent: () =>
          import('./features/admin/social-links-editor/social-links-editor.component').then(
            (m) => m.SocialLinksEditorComponent,
          ),
      },
      {
        path: 'config',
        loadComponent: () =>
          import('./features/admin/config-editor/config-editor.component').then(
            (m) => m.ConfigEditorComponent,
          ),
      },
    ],
  },

  // -- Legacy redirects (routes without lang prefix -> /es/...) --
  { path: 'projects', redirectTo: '/es/projects', pathMatch: 'full' },
  { path: 'projects/:slug', redirectTo: '/es/projects/:slug' },
  { path: 'blog', redirectTo: '/es/blog', pathMatch: 'full' },
  { path: 'blog/:slug', redirectTo: '/es/blog/:slug' },
  { path: 'cv', redirectTo: '/es/cv', pathMatch: 'full' },
  { path: 'uses', redirectTo: '/es/uses', pathMatch: 'full' },
  { path: 'stack', redirectTo: '/es/stack', pathMatch: 'full' },
  { path: 'now', redirectTo: '/es/now', pathMatch: 'full' },
  { path: 'lab', redirectTo: '/es/lab', pathMatch: 'full' },
  { path: 'guestbook', redirectTo: '/es/guestbook', pathMatch: 'full' },
  { path: 'certifications/:id', redirectTo: '/es/certifications/:id' },
  { path: 'terminal', redirectTo: '/es/terminal', pathMatch: 'full' },

  // -- Language-prefixed public routes --
  {
    path: ':lang',
    canActivate: [languageGuard],
    children: publicChildren,
  },

  // -- Catch-all 404 --
  {
    path: '**',
    loadComponent: () =>
      import('./features/not-found/not-found.component').then((m) => m.NotFoundComponent),
  },
];
